import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import BoltIcon from '@mui/icons-material/Bolt';
import React from 'react';
import { useAppSelector } from '../../app/hooks';
import {
  selectIsExpressUpsellMode,
  selectIsExpressTimerActive,
} from '../../selectors/menu';
import { selectIsCarExited } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import Colors from '../../utils/color';

/**
 * PULSE SIGNALS (HITL 3.0) — a labelled list of live order signals in the Pulse
 * card.
 *
 * Currently surfaces one signal: "Express mode", shown (with the bolt icon) when
 * express mode is ON by either trigger — an express-mode meal period or the order
 * timer elapsing (`isExpressUpsellMode || isExpressTimerActive`) — mirroring the
 * header bolt this replaces. More signals (intent, upsell state, …) land later.
 *
 * The "Signals" heading always renders; the body shows a placeholder when no
 * signal is active. Gated by hitl_3_0 at the call site (CustomerPulse).
 */
const useStyles = makeStyles((theme: Theme) => ({
  section: {
    marginTop: theme.spacing(1.75),
  },
  heading: {
    fontSize: '0.82rem',
    fontWeight: 700,
    color: Colors.mineShaft,
    marginBottom: theme.spacing(0.875),
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  icon: {
    fontSize: '1.05rem',
    color: Colors.amber,
    flexShrink: 0,
  },
  label: {
    fontSize: '0.78rem',
    color: Colors.mineShaft,
  },
  // Matches Action Center's "No actions right now" empty state.
  empty: {
    fontSize: '0.72rem',
    color: '#79808a',
    fontStyle: 'italic',
  },
}));

const PulseSignals = () => {
  const classes = useStyles();
  const isExpressUpsellMode = useAppSelector(selectIsExpressUpsellMode);
  const isExpressTimerActive = useAppSelector(selectIsExpressTimerActive);
  const carExited = useAppSelector(selectIsCarExited);

  // On car exit the order is ending/resetting — drop the signal so it doesn't
  // linger past the order.
  const expressActive =
    (isExpressUpsellMode || isExpressTimerActive) && !carExited;
  const hasSignals = expressActive;

  return (
    <div className={classes.section} data-testid="pulse-signals">
      <div className={classes.heading}>Signals</div>
      {hasSignals ? (
        <div className={classes.list}>
          {expressActive && (
            <div className={classes.row} data-testid="signal-express-mode">
              <BoltIcon className={classes.icon} />
              <span className={classes.label}>Express mode</span>
            </div>
          )}
        </div>
      ) : (
        <span className={classes.empty}>No active signals</span>
      )}
    </div>
  );
};

export default PulseSignals;
